import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Globe,
  UserPlus,
  Check,
  AtSign,
  AlertTriangle,
} from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { useEmailPreferences } from '../hooks/useEmailPreferences';
import { useMyProfile } from '../hooks/useProfile';
import { Eyebrow } from '../components/ui/Eyebrow';
import { SettingGroup } from '../components/social/SettingGroup';
import { RadioOption } from '../components/social/RadioOption';
import { ToggleRow } from '../components/social/ToggleRow';
import { Toast } from '../components/ui/Toast';
import { ResetAlbumModal } from '../components/social/ResetAlbumModal';
import { validateUsername } from '../lib/usernameModeration';
import type { AlbumVisibility } from '../types/social';

interface Props {
  user: User;
}

interface ToggleRowEmail {
  key: 'invite_accepted' | 'sticker_match' | 'weekly_digest' | 'milestones';
  title: string;
  subtitle: string;
}

const TOGGLES: ToggleRowEmail[] = [
  {
    key: 'invite_accepted',
    title: 'Convite aceito',
    subtitle: 'Saber quando alguém aceita seu convite de amizade.',
  },
  {
    key: 'sticker_match',
    title: 'Match de figurinhas',
    subtitle: 'Quando um amigo passa a ter, como repetida, alguma figurinha que você precisa.',
  },
  {
    key: 'weekly_digest',
    title: 'Resumo semanal',
    subtitle: 'Toda segunda: quantos amigos seus usaram o app na última semana.',
  },
  {
    key: 'milestones',
    title: 'Marcos do álbum',
    subtitle: 'Comemoração quando você cruzar 25%, 50%, 75% e 100% do álbum.',
  },
];

const ALBUM_OPTIONS: Array<{ value: AlbumVisibility; label: string; description: string }> = [
  { value: 'friends', label: 'Só amigos', description: 'Apenas pessoas que você adicionou. (Padrão)' },
  { value: 'friends_of_friends', label: 'Amigos e amigos de amigos', description: 'Conexões em até 2 níveis.' },
  { value: 'public', label: 'Todo mundo', description: 'Qualquer pessoa do BoraTrocar.' },
];

export default function SettingsPage({ user }: Props) {
  const { prefs, loading: prefsLoading, saving: prefsSaving, error: prefsError, update: updatePrefs } =
    useEmailPreferences(user.id);

  const { profile, loading: profLoading, saving: profSaving, savedAt, update: updateProfile } =
    useMyProfile(user.id);

  const [savedFlash, setSavedFlash] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);

  useEffect(() => {
    if (savedAt > 0) {
      setSavedFlash(true);
      const id = setTimeout(() => setSavedFlash(false), 1800);
      return () => clearTimeout(id);
    }
  }, [savedAt]);

  useEffect(() => {
    if (window.location.hash === '#privacidade') {
      const el = document.getElementById('privacidade');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [profile]);

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-green-700 text-white sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-shell mx-auto px-4 md:px-6 py-3.5 flex items-center gap-3">
          <Link
            to="/album"
            aria-label="Voltar ao álbum"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border-[1.5px] border-white/40 text-white hover:bg-white/10"
          >
            <ArrowLeft size={16} strokeWidth={2.25} />
          </Link>
          <h1 className="text-base font-display font-semibold tracking-[0.02em]">Configurações</h1>
          {savedFlash && (
            <span className="ml-auto inline-flex items-center gap-1 text-caption text-white/85 font-display font-semibold">
              <Check size={14} strokeWidth={2.25} /> Salvo
            </span>
          )}
        </div>
      </header>

      <main className="max-w-shell mx-auto px-4 md:px-6 pt-6 pb-24">
        <section className="rounded-2xl border border-black/5 bg-white shadow-sm p-5 md:p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-display font-semibold text-zinc-900">E-mails do BoraTrocar</h2>
            {prefsSaving && <Loader2 size={16} className="animate-spin text-zinc-400" />}
          </div>
          <p className="text-sm text-zinc-600 mb-5">
            Escolha quais avisos por e-mail você quer receber. Suas mudanças são salvas
            automaticamente.
          </p>

          {prefsError && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              Não foi possível salvar: {prefsError}
            </div>
          )}

          <ul className="divide-y divide-black/5">
            {TOGGLES.map((t) => (
              <li key={t.key} className="flex items-start justify-between gap-4 py-4">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-zinc-900">{t.title}</div>
                  <div className="text-sm text-zinc-600 mt-0.5">{t.subtitle}</div>
                </div>
                <Toggle
                  checked={prefs[t.key] && !prefs.unsubscribed_all}
                  disabled={prefsLoading || prefs.unsubscribed_all}
                  onChange={(v) => updatePrefs({ [t.key]: v })}
                />
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-6 rounded-2xl border border-black/5 bg-white shadow-sm p-5 md:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-display font-semibold text-zinc-900">
                Descadastrar de tudo
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                Para de receber qualquer e-mail do BoraTrocar. Você pode reativar a qualquer momento.
              </p>
            </div>
            <Toggle
              checked={prefs.unsubscribed_all}
              disabled={prefsLoading}
              onChange={(v) => updatePrefs({ unsubscribed_all: v })}
            />
          </div>
        </section>

        <section id="privacidade" className="mt-8 scroll-mt-24">
          <Eyebrow>Controle quem vê o quê</Eyebrow>
          <div className="flex items-center justify-between mt-1.5 mb-1">
            <h2 className="font-display font-bold text-h2 text-ink-900 tracking-[-0.01em]">
              Privacidade
            </h2>
            {profSaving && <Loader2 size={14} className="animate-spin text-ink-500" />}
          </div>
          <p className="text-small text-ink-500 mb-4 leading-snug">
            Controle quem vê suas informações e quem pode entrar em contato. As mudanças são
            aplicadas imediatamente.
          </p>

          {profLoading || !profile ? (
            <div className="flex items-center justify-center py-12 text-ink-500">
              <Loader2 size={20} className="animate-spin text-green-700" />
            </div>
          ) : (
            <>
              <SettingGroup
                icon={Globe}
                title="Quem vê meu álbum"
                description="Suas figurinhas, repetidas e faltantes."
              >
                {ALBUM_OPTIONS.map((o) => (
                  <RadioOption
                    key={o.value}
                    active={profile.album_visibility === o.value}
                    onClick={() => updateProfile({ album_visibility: o.value })}
                    title={o.label}
                    description={o.description}
                  />
                ))}
              </SettingGroup>

              <SettingGroup icon={UserPlus} title="Sugestões">
                <ToggleRow
                  title="Aparecer em sugestões para amigos de amigos"
                  description="Pessoas que têm amigos em comum com você podem te ver em sugestões."
                  value={profile.show_in_fof_suggestions ?? true}
                  onChange={(v) => updateProfile({ show_in_fof_suggestions: v })}
                />
              </SettingGroup>

              <UsernameSection
                currentUsername={profile.username ?? null}
                onSaved={() => setToastMessage('Username atualizado.')}
                save={async (next) => {
                  const result = await updateProfile({ username: next });
                  if (result.ok) return { ok: true };
                  if (
                    result.code === '23505' ||
                    result.message?.toLowerCase().includes('unique')
                  ) {
                    return { ok: false, reason: 'taken' };
                  }
                  return { ok: false, reason: 'error' };
                }}
              />
            </>
          )}
        </section>

        <section className="mt-8">
          <Eyebrow>Zona de risco</Eyebrow>
          <h2 className="font-display font-bold text-h2 text-ink-900 tracking-[-0.01em] mt-1.5 mb-1">
            Ações irreversíveis
          </h2>
          <p className="text-small text-ink-500 mb-4 leading-snug">
            Use com cuidado. Estas ações não podem ser desfeitas.
          </p>

          <button
            type="button"
            onClick={() => setResetModalOpen(true)}
            className="w-full text-left px-4 py-3 rounded-lg border border-ruby-300 bg-ruby-50 hover:bg-ruby-100 text-ruby-700 font-display font-semibold btn-focus transition-colors"
          >
            Zerar álbum
            <span className="block text-small font-normal text-ruby-700 mt-1 opacity-80">
              Apaga suas figurinhas, wishlist, feed e conquistas. Mantém amigos e trocas.
            </span>
          </button>
        </section>
      </main>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}

      {resetModalOpen && (
        <ResetAlbumModal user={user} onClose={() => setResetModalOpen(false)} />
      )}
    </div>
  );
}

interface UsernameSectionProps {
  currentUsername: string | null;
  onSaved: () => void;
  save: (next: string) => Promise<{ ok: true } | { ok: false; reason: 'taken' | 'error' }>;
}

const ALLOWED_CHAR_RE = /[a-z0-9_]/;

function sanitizeUsernameInput(raw: string): string {
  const lower = raw.toLowerCase();
  let out = '';
  for (const ch of lower) {
    if (ALLOWED_CHAR_RE.test(ch)) out += ch;
    if (out.length >= 20) break;
  }
  return out;
}

function UsernameSection({ currentUsername, onSaved, save }: UsernameSectionProps) {
  const [value, setValue] = useState(currentUsername ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(currentUsername ?? '');
  }, [currentUsername]);

  const dirty = value !== (currentUsername ?? '');
  const canSave = dirty && !saving && value.length > 0;

  const handleSave = async () => {
    setError(null);
    const validation = validateUsername(value);
    if (!validation.ok) {
      switch (validation.reason) {
        case 'too_short':
          setError('Mínimo 4 caracteres.');
          return;
        case 'too_long':
          setError('Máximo 20 caracteres.');
          return;
        case 'invalid_chars':
          setError('Use apenas letras minúsculas, números e _.');
          return;
        case 'reserved_name':
          setError(
            'Esse nome é muito comum. Adicione um sobrenome ou número (ex.: andresilva, andre_g).'
          );
          return;
        case 'banned_word':
          setError('Esse nome de usuário não é permitido. Escolha outro.');
          return;
      }
    }

    setSaving(true);
    const result = await save(value);
    setSaving(false);

    if (!result.ok) {
      if (result.reason === 'taken') {
        setError('Esse @username já está em uso.');
      } else {
        setError('Não foi possível salvar. Tente novamente.');
      }
      return;
    }
    onSaved();
  };

  return (
    <SettingGroup icon={AtSign} title="Username" description="Como amigos te encontram no app.">
      <div className="px-4 py-3">
        <label className="block">
          <span className="sr-only">Username</span>
          <div
            className={[
              'h-11 px-3 flex items-center gap-1 rounded-md border-[1.5px] bg-white',
              error ? 'border-ruby-500' : 'border-ink-300 focus-within:border-green-700',
            ].join(' ')}
          >
            <span className="font-display font-bold text-ink-500 text-body select-none">@</span>
            <input
              type="text"
              value={value}
              onChange={(e) => {
                setValue(sanitizeUsernameInput(e.target.value));
                setError(null);
              }}
              placeholder="seunome"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={20}
              aria-invalid={!!error}
              className="flex-1 bg-transparent text-body font-body text-ink-900 outline-none"
            />
          </div>
        </label>

        {error && (
          <p className="text-small text-ruby-700 mt-2 inline-flex items-center gap-1.5">
            <AlertTriangle size={12} strokeWidth={2.25} /> {error}
          </p>
        )}

        <p className="text-caption text-ink-500 mt-2 leading-snug">
          Use letras minúsculas, números e underscore (_). De 4 a 20 caracteres.
        </p>

        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={[
            'mt-3 h-10 px-4 rounded-md font-display font-bold inline-flex items-center justify-center gap-2 btn-focus',
            canSave
              ? 'bg-green-700 text-white hover:bg-green-900'
              : 'bg-ink-100 text-ink-500 cursor-not-allowed',
          ].join(' ')}
        >
          {saving ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Salvando...
            </>
          ) : (
            'Salvar'
          )}
        </button>
      </div>
    </SettingGroup>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors btn-focus',
        checked ? 'bg-green-700' : 'bg-zinc-300',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}
